import React, { useMemo, useState } from "react";
import {
  ContributorStack,
  QokrAvatar,
  personLabel,
} from "../QokrPrimitives/QokrPrimitives";
import QokrOkrDetails from "../QokrOkrDetails/QokrOkrDetails";
import "../QokrPrimitives/_qokr-avatar.scss";
import "../QokrPrimitives/_qokr-pill.scss";
import "./_qokr-alignment-view.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The team board's second view: how the team's OKRs hang off the company
// OKRs (`GET /team-okrs`, §7.3). One card per company objective with its
// linked individual objectives as a tree under it, then the objectives not
// linked to any. Read only.
//
//   • Summary strip: company objectives, linked team objectives, gaps.
//   • A company card opens / closes (smooth height); a team row opens its key
//     results and initiatives. The viewer's rows are marked "You".
//   • Motion: cards rise in one after another, the tree's line draws down,
//     rows slide in from it. All of it is off under prefers-reduced-motion.
// ═══════════════════════════════════════════════════════════════════════════

const same = (a, b) => a != null && b != null && String(a) === String(b);

const RELATIONSHIP_LABEL = {
  owner: "Owner",
  contributor: "Contributor",
};

const krCount = (objective) =>
  (objective?.key_results || []).filter(Boolean).length;

const TeamRow = ({ objective, viewerId, index }) => {
  const [open, setOpen] = useState(false);
  const mine = same(objective.member?.user_id, viewerId);
  const relationship = RELATIONSHIP_LABEL[objective.relationship];
  const krs = krCount(objective);
  return (
    <li
      className={`qokr-align__row${mine ? " is-mine" : ""}${
        open ? " is-open" : ""
      }`}
      style={{ "--qokr-i": index }}
    >
      <button
        type="button"
        className="qokr-align__row-head"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <QokrAvatar person={objective.member} size="sm" />
        <span className="qokr-align__row-main">
          <span className="qokr-align__row-who">
            {mine ? "You" : personLabel(objective.member) || "Team member"}
            {relationship ? (
              <span className="qokr-align__role">{relationship}</span>
            ) : null}
          </span>
          <span className="qokr-align__row-text">{objective.objective}</span>
        </span>
        <span className="qokr-align__row-side">
          {krs ? (
            <span className="qokr-align__krs">
              {krs} KR{krs === 1 ? "" : "s"}
            </span>
          ) : null}
          <i
            className="fa-solid fa-chevron-down qokr-align__chev"
            aria-hidden="true"
          />
        </span>
      </button>
      <div className="qokr-align__collapse" aria-hidden={!open}>
        <div className="qokr-align__collapse-inner">
          <div className="qokr-align__details">
            <QokrOkrDetails
              keyResults={objective.key_results}
              initiatives={objective.initiatives}
            />
          </div>
        </div>
      </div>
    </li>
  );
};

const CompanyCard = ({ company, number, viewerId, index, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const linked = useMemo(
    () => company.individual_objectives || [],
    [company.individual_objectives],
  );
  const people = useMemo(() => {
    const seen = new Map();
    linked.forEach((o) => {
      if (o.member?.user_id != null && !seen.has(String(o.member.user_id))) {
        seen.set(String(o.member.user_id), o.member);
      }
    });
    return [...seen.values()];
  }, [linked]);
  const gap = linked.length === 0;

  return (
    <section
      className={`qokr-align__card${open ? " is-open" : ""}${
        gap ? " is-gap" : ""
      }`}
      style={{ "--qokr-i": index }}
    >
      <button
        type="button"
        className="qokr-align__card-head"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="qokr-align__num">O{number}</span>
        <span className="qokr-align__card-main">
          <span className="qokr-align__card-title">{company.objective}</span>
          <span className="qokr-align__card-meta">
            {company.owner ? (
              <span className="qokr-align__owner">
                <QokrAvatar person={company.owner} size="xs" />
                {personLabel(company.owner)}
              </span>
            ) : null}
            {/* Only the gap is called out — a count on every card was
                noise (2026-10-28). */}
            {gap ? (
              <span className="qokr-align__coverage is-gap">
                <i
                  className="fa-solid fa-circle-exclamation"
                  aria-hidden="true"
                />
                No team objectives yet
              </span>
            ) : null}
            {people.length ? (
              <ContributorStack people={people} max={4} />
            ) : null}
          </span>
        </span>
        <i
          className="fa-solid fa-chevron-down qokr-align__chev"
          aria-hidden="true"
        />
      </button>

      <div className="qokr-align__collapse" aria-hidden={!open}>
        <div className="qokr-align__collapse-inner">
          {gap ? (
            <p className="qokr-align__gap-note">
              Nobody has linked an objective to this yet.
            </p>
          ) : (
            <ul className="qokr-align__tree">
              {linked.map((objective, i) => (
                <TeamRow
                  key={objective.objective_id}
                  objective={objective}
                  viewerId={viewerId}
                  index={i}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

const QokrAlignmentView = ({ teamOkrs, viewerId }) => {
  const companies = useMemo(
    () => teamOkrs?.company_okrs || [],
    [teamOkrs?.company_okrs],
  );
  const notLinked = teamOkrs?.not_linked || [];

  const totals = useMemo(() => {
    const linked = companies.reduce(
      (n, c) => n + (c.individual_objectives || []).length,
      0,
    );
    const gaps = companies.filter(
      (c) => !(c.individual_objectives || []).length,
    ).length;
    return { linked, gaps };
  }, [companies]);

  const shown = companies.map((company, i) => ({ company, number: i + 1 }));
  const shownNotLinked = notLinked;

  return (
    <div className="qokr-align">
      <div className="qokr-align__summary">
        <div className="qokr-align__stat">
          <b>{companies.length}</b>
          <span>company objective{companies.length === 1 ? "" : "s"}</span>
        </div>
        <span className="qokr-align__stat-sep" aria-hidden="true" />
        <div className="qokr-align__stat">
          <b>{totals.linked}</b>
          <span>team objective{totals.linked === 1 ? "" : "s"} linked</span>
        </div>
        <span className="qokr-align__stat-sep" aria-hidden="true" />
        <div
          className={`qokr-align__stat${totals.gaps ? " is-warn" : " is-ok"}`}
        >
          <b>{totals.gaps}</b>
          <span>without team objectives</span>
        </div>
      </div>

      <div className="qokr-align__list">
        {shown.map(({ company, number }, i) => (
          <CompanyCard
            key={company.objective_id}
            company={company}
            number={number}
            viewerId={viewerId}
            index={i}
            defaultOpen={i < 2}
          />
        ))}

        {shownNotLinked.length ? (
          <section
            className="qokr-align__card qokr-align__card--loose is-open"
            style={{ "--qokr-i": shown.length }}
          >
            <div className="qokr-align__loose-head">
              <i className="fa-solid fa-link-slash" aria-hidden="true" />
              Not linked to a company objective
              <span className="qokr-align__chip-count">
                {shownNotLinked.length}
              </span>
            </div>
            <ul className="qokr-align__tree qokr-align__tree--loose">
              {shownNotLinked.map((objective, i) => (
                <TeamRow
                  key={objective.objective_id}
                  objective={objective}
                  viewerId={viewerId}
                  index={i}
                />
              ))}
            </ul>
          </section>
        ) : null}

        {!shown.length && !shownNotLinked.length ? (
          <p className="qokr-align__empty">No company objectives yet.</p>
        ) : null}
      </div>
    </div>
  );
};

export default QokrAlignmentView;

// ─── Adapters ────────────────────────────────────────────────────────────────
// The view reads the team-okrs shape (§7.3): `{ company_okrs: [{ …,
// individual_objectives }], not_linked }`. The live review snapshot (§10.2)
// already nests that way; the coach's review payload (§9.3) lists individual
// objectives flat with `company_objective_id`.
export const alignmentFromLive = (details) =>
  details
    ? {
        company_okrs: details.company_objectives || [],
        not_linked: details.not_linked || [],
      }
    : null;

export const alignmentFromReview = (review) => {
  if (!review) return null;
  const companies = review.company_objectives || [];
  const individuals = review.individual_objectives || [];
  const ids = new Set(companies.map((c) => String(c.objective_id)));
  return {
    company_okrs: companies.map((company) => ({
      ...company,
      individual_objectives: individuals.filter(
        (o) => String(o.company_objective_id) === String(company.objective_id),
      ),
    })),
    not_linked: individuals.filter(
      (o) =>
        o.company_objective_id == null ||
        !ids.has(String(o.company_objective_id)),
    ),
  };
};
